import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {
  extensionPublicHubTitlePrefix,
  Hub,
  HubProperties,
  newsCompanyId
} from 'yougile-model'
import {Sync} from '../client/sync'

/**
 * After this timeout news item is not considered new even
 * if the user haven’t seen it.
 */
const newsActualTimeout = 60 * Time.day

export enum NewsSeenReason {
  NoData = 'noData',
  NotActual = 'notActual',
  IncludedInSeen = 'includedInSeen',
  Unseen = 'unseen'
}

export class News {
  static add({
    title,
    dateStr,
    type,
    lang
  }: {
    title: string
    dateStr: string
    type: string
    lang: string
  }): void {
    const box = Sync.newsSandbox
    if (!box) {
      return
    }
    const item = new Hub(box.companyId, title)
    item.properties[HubProperties.newsTaskDate] = dateStr
    item.properties[HubProperties.newsTaskType] = type
    item.properties[HubProperties.newsTaskLang] = lang
    box.add(item, box.companyId, false, false)
  }

  /**
   * News item is not too old
   */
  private static isNewsActual(hub: Hub): boolean {
    if (!hub || Sync.getCurrentUser().timestamp > hub.timestamp) {
      return false
    }
    return hub.timestamp > Time.now() - newsActualTimeout
  }

  private static getProfileHub(): Hub {
    const box = Sync.getProfile()
    return box.getHub(box.companyId)
  }

  static isUnseen(hub: Hub): boolean {
    if (!News.isNewsActual(hub)) {
      return false
    }
    const profile = News.getProfileHub()
    return !profile.data?.seenNews?.includes(hub.id)
  }

  static isUnseenReason(hub: Hub): NewsSeenReason {
    if (!News.isNewsActual(hub)) {
      return NewsSeenReason.NotActual
    }
    const profile = News.getProfileHub()
    if (!profile.data?.seenNews?.includes(hub.id)) {
      return NewsSeenReason.Unseen
    }
    return NewsSeenReason.IncludedInSeen
  }

  static markSeen(hub: Hub): void {
    if (!News.isUnseen(hub)) {
      return
    }
    const profile = News.getProfileHub()
    profile.data.seenNews ??= []
    profile.data.seenNews.push(hub.id)
    profile.commit()
  }

  static isClosed(hub: Hub): boolean {
    const profile = News.getProfileHub()
    return profile.data.closedNews?.includes(hub.id)
  }

  static markClosed(hub: Hub): void {
    if (News.isClosed(hub)) {
      return
    }
    const profile = News.getProfileHub()
    profile.data.closedNews ??= []
    profile.data.closedNews.push(hub.id)
    profile.commit()
  }

  static isNewsHub(hub: Hub): boolean {
    return hub?.companyId === newsCompanyId
  }

  static list(): Hub[] {
    if (!Sync.newsSandbox) {
      return []
    }
    const supportedLangs = [Lang.getLang(), 'ru']
    return Sync.newsSandbox
      .getChildrenHubs(Sync.newsSandbox.companyId)
      .filter(
        (h: Hub) =>
          (Sync.isNewsEditor || supportedLangs.includes(News.getLang(h))) &&
          !h.title.startsWith(extensionPublicHubTitlePrefix)
      )
      .sort((h1: Hub, h2: Hub) => h2.timestamp - h1.timestamp)
  }

  static getType(hub: Hub): string {
    return hub.properties[HubProperties.newsTaskType] as string
  }

  static getDateString(hub: Hub): string {
    return hub.properties[HubProperties.newsTaskDate] as string
  }

  static getLang(hub: Hub): string {
    return hub.properties[HubProperties.newsTaskLang] as string
  }

  static isWithPopup(hub: Hub | undefined): boolean {
    return !!hub?.properties?.[HubProperties.newsTaskPopup]
  }

  static setWithPopup(hub: Hub, value: boolean): void {
    hub.properties[HubProperties.newsTaskPopup] = value
    hub.commit()
  }

  static canEdit(): boolean {
    return Sync.isNewsEditor
  }
}
